// @owners { team: patients-team }
import { useEffect, useMemo } from 'react';
import { useCartDeliveryMethods } from '~shared/features/cart/queries/useCartDeliveryMethods';
import { getShowingValidationErrors } from '~shared/features/checkout/selectors/getCheckout';
import { getOrder, getOrderAddressId } from '~shared/features/checkout/selectors/getOrder';
import { getOrderIndex } from '~shared/features/checkout/selectors/getOrdersByIndex';
import getShipmentID from '~shared/features/next-available-date/selectors/getShipmentId';
import { getPrescriptionsByIDs } from '~shared/features/prescriptions/selectors/getPrescriptionByID';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useSelectorShared } from '~shared/store';
import { useQueryMailRequestRequired } from '../queries/queries';

export const useCheckoutMailRequest = () => {
  const { trackEvent } = useAnalytics();
  const order = useSelectorShared(getOrder);
  const mailRequestConfirmed = order.mail_request_confirmed;
  const orderIndex = useSelectorShared(getOrderIndex);
  const shipmentID = useSelectorShared(getShipmentID);
  const addressID = useSelectorShared(getOrderAddressId);
  const showingValidationErrors = useSelectorShared(getShowingValidationErrors);
  const { cartItemsByDeliveryMethod, deliveryMethods } = useCartDeliveryMethods();
  const isLastShipment = orderIndex === deliveryMethods.length - 1;
  // Get prescriptions for the current shipment
  const deliveryMethod = deliveryMethods[orderIndex];
  const deliveryMethodName = deliveryMethod?.name;
  const prescriptionItemsForShipment = deliveryMethodName ? (cartItemsByDeliveryMethod[deliveryMethodName] ?? []) : [];
  const prescriptionIDs = prescriptionItemsForShipment.map((item) => item.resource_id);
  const prescriptions = useSelectorShared((state) => getPrescriptionsByIDs(state, prescriptionIDs));
  // Determine if it's a compound-only shipment
  const isOnCompoundCheckoutPage = useMemo(() => {
    return (prescriptions || []).every((prescription) => prescription.is_compound);
  }, [prescriptions]);

  const { mailRequestRequired, mailRequestRequiredMessage } = useQueryMailRequestRequired({
    addressID,
    prescriptionIDs,
    deliveryMethod: order.delivery_method,
    shipmentID,
    isOnCompoundCheckoutPage,
    isLastShipment,
  });

  const hasMailRequestConfirmedError =
    mailRequestRequired && !mailRequestConfirmed && !isOnCompoundCheckoutPage && isLastShipment;
  const showMailRequestConfirmedError = showingValidationErrors && hasMailRequestConfirmedError;

  useEffect(() => {
    trackEvent({
      event: EVENTS.CHECKOUT__MAIL_REQUEST_ELIGIBILITY,
      params: {
        mailRequestRequired,
        hasMailRequestConfirmedError,
        showMailRequestConfirmedError,
        addressID,
        prescriptionIDs,
        deliveryMethod: order.delivery_method,
        shipmentID,
        isOnCompoundCheckoutPage,
        isLastShipment,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mailRequestRequired,
    hasMailRequestConfirmedError,
    showMailRequestConfirmedError,
    addressID,
    prescriptionIDs.length,
    order.delivery_method,
    shipmentID,
    isOnCompoundCheckoutPage,
    isLastShipment,
  ]);

  return {
    mailRequestRequired,
    mailRequestRequiredMessage,
    hasMailRequestConfirmedError,
    showMailRequestConfirmedError,
  };
};
